import React, { Suspense, useRef, useContext } from "react"
import { MapOptions, useMap } from "hooks/use_map"
import { ScenariosDropdown } from "../../../components/scenario_dropdown"
import { SymbolIcon } from "@radix-ui/react-icons"
import useActiveFacet from "hooks/use_active_facet"
import useFilters from "hooks/use_filters"
import { Toaster } from "react-hot-toast"
import { useQuery } from "@blitzjs/rpc"
import getScenario from "app/scenarios/queries/getScenario"
import { CityContext, MapContext } from "app/features/regional-map/regional-map.state"
import useMapTooltip from "hooks/use_map_tooltip"
import usePaint from "hooks/use_paint"
import useFacets from "hooks/use_facets"
import SideBar, { MapHeader } from "../../../components/sidebar"
import { scenarioIdDecode } from "app/utils/scenario_id"
import { facetsAtom } from "app/state"
import { useAtomValue } from "jotai"
import { addMapLayers } from "app/features/regional-map/regional-map.utils"
import { useMediaQuery } from "@material-ui/core"
import GeoCoder from "../../../components/geocoder"
import NavMenu from "../../../components/nav_menu"
import { useRouter } from "next/router"
import { useParam } from "@blitzjs/next"
import { useTranslation } from "react-i18next"

const OPTIONS: Omit<MapOptions, "extent"> = {
  rememberPosition: true,
  hash: false,
  zoomControl: true,
}

export default function TesaMap({ children }: { children: React.ReactNode }) {
  const city = useContext(CityContext)!
  const facetsLoaded = useFacets(city)
  const facets = useAtomValue(facetsAtom)!
  const isMobile = useMediaQuery("(max-width: 640px)")
  const { t } = useTranslation(["common"])
  const mapDiv = useRef<HTMLDivElement>(null)
  const { scenarioId } = useRouter().query as Record<string, string | undefined>

  const [scenario] = useQuery(
    getScenario,
    { id: scenarioIdDecode(scenarioId) },
    { enabled: !!scenarioId }
  )

  const { map, currentLevel, baseMapStyleIsLoaded, baseMapStyle, setBaseMapStyle } = useMap({
    mapDiv,
    scenario,
    options: {
      ...OPTIONS,
      extent: city.initialExtent,
    },
    facetsLoaded,
    city,
    addMapLayers: addMapLayers,
  })
  const { activeFacet, setActiveFacet } = useActiveFacet({
    map: map,
    defaultValue: facets[0]!,
    zoomLevel: currentLevel,
    baseMapStyleIsLoaded,
    facetsLoaded,
  })
  const cityId = String(useParam("city"))

  usePaint({ map, scenario, baseMapStyle, baseMapStyleIsLoaded })
  useMapTooltip({ map: map!, facet: activeFacet!, scenario })
  useFilters({ map, baseMapStyleIsLoaded, currentLevel })

  return (
    <>
      {/* 30px is the height of the release banner */}
      <div className="inset-0 flex min-h-screen flex-col sm:h-screen sm:flex-row">
        {!isMobile && (
          <div className="flex flex-col divide-y divide-gray-300 overflow-y-auto border-gray-400 bg-[#FCFDFE] transition-all sm:w-96 sm:border-r sm:shadow-md">
            <MapHeader city={city} />
            <Suspense fallback={null}>
              <GeoCoder map={map} city={city} />
            </Suspense>
            <SideBar
              map={map}
              city={city}
              activeFacet={activeFacet}
              setActiveFacet={setActiveFacet}
              baseMapStyle={baseMapStyle}
              setBaseMapStyle={setBaseMapStyle}
              currentLevel={currentLevel}
            />
          </div>
        )}

        {isMobile && (
          <div>
            <MapHeader city={city} />
            <Suspense fallback={null}>
              <GeoCoder map={map} city={city} />
            </Suspense>
          </div>
        )}

        <div ref={mapDiv} className="h-[50vh] sm:h-auto sm:flex-auto" />

        <div className="right-4 top-[5.5rem] max-h-[90vh] w-96 overflow-y-auto pb-5 sm:absolute">
          <div className="pointer-events-auto bg-white shadow-md">
            <MapContext.Provider value={map}>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center gap-x-2 p-2 py-4 text-gray-500">
                    <SymbolIcon />
                    Loading…
                  </div>
                }
              >
                {children}
              </Suspense>
            </MapContext.Provider>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0 z-10 flex w-full flex-row justify-center">
          {/* adjust top right buttons for TESAs with banners */}
          <div
            className={`absolute ${
              cityId === "washington" ? "top-10" : "top-2"
            } pointer-events-auto right-2 flex h-[36px] flex-row`}
          >
            <MapContext.Provider value={map}>
              <Suspense fallback={null}>
                <div className="mr-2 flex items-center rounded-full bg-white px-2 shadow-md">
                  <ScenariosDropdown />
                </div>
                <div className="flex items-center rounded-full bg-white px-2 shadow-md">
                  <NavMenu displayText={t("common:menu")} />
                </div>
              </Suspense>
            </MapContext.Provider>
          </div>
        </div>

        {isMobile && (
          <div className="h-full">
            <SideBar
              map={map}
              city={city}
              activeFacet={activeFacet}
              setActiveFacet={setActiveFacet}
              baseMapStyle={baseMapStyle}
              setBaseMapStyle={setBaseMapStyle}
              currentLevel={currentLevel}
            />
          </div>
        )}
        <Toaster />
      </div>
    </>
  )
}
